import { useCallback, useEffect, useState } from "react";
import type {
  CoachCycleView,
  CycleStep,
  DoctorCycleView,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import {
  fetchPatientCycleView,
  ForbiddenError,
  UnauthorizedError,
} from "@/lib/clinician-api";

// TES-749 (TES-818) — the recurring treatment month, read as a loop.
//
// The route returns the coach's neutral shape or the doctor's superset, chosen
// by the JWT role — never by anything this component asks for. So the panel
// does not decide what a coach may see; it renders whichever shape arrived,
// and the extra clinical block simply is not there for a coach.
//
// `cycle_not_found` is an ordinary answer. A patient who has not been
// activated yet has no cycle, and that is an empty state, not a failure.

const STEP_LABELS: Record<CycleStep, string> = {
  awaiting_activation: "Väntar på aktivering",
  ongoing_treatment: "Löpande behandling",
  monthly_checkin: "Månadsincheckning",
  medical_evidence: "Medicinskt underlag",
  doctor_review: "Läkarbedömning",
  renewal_decision: "Receptförnyelse",
  paused: "Pausad",
  ended: "Avslutad",
};

// The five steps that repeat inside Aktiv behandling, in the order the
// coach-admin package describes them. `awaiting_activation`, `paused` and
// `ended` sit OUTSIDE the loop, so they are shown as a status line instead of
// lighting up a step that is not running.
const LOOP_STEPS: CycleStep[] = [
  "ongoing_treatment",
  "monthly_checkin",
  "medical_evidence",
  "doctor_review",
  "renewal_decision",
];

function isDoctorView(
  view: CoachCycleView | DoctorCycleView,
): view is DoctorCycleView {
  // `state` is the raw cycle state, and it exists only on the doctor's shape.
  return "state" in view;
}

export function PatientCycleView({ patientId }: { patientId: string }) {
  const { signOut } = useAuth();
  const [view, setView] = useState<CoachCycleView | DoctorCycleView | null>(
    null,
  );
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Same rule as the memory panel: a 403 means this employee's role is not one
  // the route serves, which is a fact about the viewer rather than an error to
  // put in front of them.
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    try {
      setView(await fetchPatientCycleView(patientId));
      setMissing(false);
      setError(null);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      if (e instanceof ForbiddenError) {
        setForbidden(true);
        return;
      }
      if (e instanceof Error && e.message === "cycle_not_found") {
        setMissing(true);
        return;
      }
      setError(
        e instanceof Error ? e.message : "Kunde inte hämta behandlingsmånaden.",
      );
    }
  }, [patientId, signOut]);

  useEffect(() => {
    void load();
  }, [load]);

  if (forbidden) return null;

  return (
    <section className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-md">
      <div className="flex flex-wrap items-center justify-between gap-sm">
        <h2 className="font-display text-lg">Behandlingsmånaden</h2>
        {view ? (
          <span className="text-xs text-textSecondary">
            Månad {view.treatmentMonth} · uppdaterad{" "}
            {view.stateChangedAt.slice(0, 10)}
          </span>
        ) : null}
      </div>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-sm text-sm">
          {error}
        </div>
      ) : null}

      {missing ? (
        <p className="text-sm text-textSecondary">
          Ingen behandlingsmånad ännu — patienten har inte fått något recept.
        </p>
      ) : !view && !error ? (
        <p className="text-sm text-textSecondary">Hämtar behandlingsmånaden…</p>
      ) : !view ? null : (
        <>
          <StepStrip step={view.step} />

          <dl className="grid grid-cols-2 gap-x-md gap-y-xs text-sm">
            <dt className="text-textSecondary">Steg</dt>
            <dd className="text-textPrimary">{STEP_LABELS[view.step]}</dd>
            <dt className="text-textSecondary">Månadsincheckning</dt>
            <dd className="text-textPrimary">
              {view.monthlyCheckInSubmitted ? "Inskickad" : "Inte inskickad"}
            </dd>
            <dt className="text-textSecondary">Till förnyelse</dt>
            <dd
              className={
                view.daysUntilRenewal !== null && view.daysUntilRenewal <= 0
                  ? "text-danger"
                  : "text-textPrimary"
              }
            >
              {/* A negative number is a real answer — an expired recept the
                  patient is still inside is exactly what both roles need to
                  see, so it is spelled out rather than clamped to zero. */}
              {view.daysUntilRenewal === null
                ? "—"
                : view.daysUntilRenewal <= 0
                  ? `Passerat med ${Math.abs(view.daysUntilRenewal)} dagar`
                  : `${view.daysUntilRenewal} dagar`}
            </dd>
          </dl>

          {isDoctorView(view) ? <DoctorBlock view={view} /> : null}
        </>
      )}
    </section>
  );
}

function StepStrip({ step }: { step: CycleStep }) {
  const inLoop = LOOP_STEPS.includes(step);
  if (!inLoop) {
    return (
      <p className="rounded-md bg-surfaceMuted p-sm text-sm text-textSecondary">
        {STEP_LABELS[step]} — månadsloopen är inte igång.
      </p>
    );
  }
  return (
    <ol className="flex flex-wrap gap-xs">
      {LOOP_STEPS.map((s) => (
        <li
          key={s}
          aria-current={s === step ? "step" : undefined}
          className={`rounded-pill px-sm py-[2px] text-xs ${
            s === step
              ? "bg-brand text-white"
              : "bg-surfaceMuted text-textSecondary"
          }`}
        >
          {STEP_LABELS[s]}
        </li>
      ))}
    </ol>
  );
}

// Everything below is a clinical judgement, and the type is what keeps it away
// from the coach: these fields exist only on the doctor's shape.
function DoctorBlock({ view }: { view: DoctorCycleView }) {
  return (
    <div className="flex flex-col gap-md border-t border-surfaceMuted pt-md">
      <dl className="grid grid-cols-2 gap-x-md gap-y-xs text-sm">
        <dt className="text-textSecondary">Ordination</dt>
        <dd className="text-textPrimary">{view.prescription}</dd>
        <dt className="text-textSecondary">Giltigt till</dt>
        <dd className="text-textPrimary">
          {view.expiresAt ? view.expiresAt.slice(0, 10) : "—"}
        </dd>
      </dl>

      {view.renewal ? (
        <div>
          <h3 className="mb-xs text-xs font-semibold uppercase tracking-wide text-textSecondary">
            Månadens incheckning
          </h3>
          <dl className="grid grid-cols-2 gap-x-md gap-y-xs text-sm">
            <dt className="text-textSecondary">Inskickad</dt>
            <dd className="text-textPrimary">
              {view.renewal.submittedAt.slice(0, 10)}
            </dd>
            <dt className="text-textSecondary">Vikt</dt>
            <dd className="text-textPrimary">
              {view.renewal.currentWeightKg !== null
                ? `${view.renewal.currentWeightKg} kg`
                : "—"}
            </dd>
            <dt className="text-textSecondary">Effekt</dt>
            <dd className="text-textPrimary">
              {view.renewal.doingWell ?? "—"}
            </dd>
            <dt className="text-textSecondary">Biverkningar</dt>
            <dd className="text-textPrimary">
              {view.renewal.sideEffects ?? "—"}
            </dd>
            <dt className="text-textSecondary">Frågor till läkaren</dt>
            <dd className="text-textPrimary">
              {view.renewal.questionsForDoctor ?? "—"}
            </dd>
          </dl>
        </div>
      ) : null}

      <div>
        <h3 className="mb-xs text-xs font-semibold uppercase tracking-wide text-textSecondary">
          Tidigare beslut
        </h3>
        {view.previousDecisions.length === 0 ? (
          <p className="text-sm text-textSecondary">
            Ingen tidigare behandlingsmånad.
          </p>
        ) : (
          <ul className="flex flex-col gap-xs">
            {view.previousDecisions.map((d) => (
              <li key={d.cycleNumber} className="text-sm text-textPrimary">
                Månad {d.cycleNumber}: {d.prescription}{" "}
                <span className="text-xs text-textSecondary">
                  ({d.decidedAt ? d.decidedAt.slice(0, 10) : "inget datum"})
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
